/**
 * The tutoring session, seen from the tablet (SPEC §9, `core-api` §6).
 *
 * Down the wire: SSE. Up: POST. Reconnection is the browser's job, resumption
 * is ours — and it is a single number, the id of the last **persisted** event.
 *
 * The subtlety worth stating once: `tutor.fragment` events carry no `id`. They
 * are the message currently being generated, buffered server-side and resent
 * whole on reconnection. They must therefore never advance the cursor, or a
 * reconnection would skip the final `tutor.message` and the child would be left
 * with a half sentence that never settles.
 */

import { buildUrl } from "@/api/client";

export type SessionEventType =
  | "session.started"
  | "child.message"
  | "tutor.fragment"
  | "tutor.message"
  | "exercise.served"
  | "hint.delivered"
  | "correction";

export type SessionEvent = {
  id: number;
  seq: number;
  type: SessionEventType;
  payload: Record<string, unknown>;
  at: string | null;
};

export type StreamHandlers = {
  /** A persisted event. Delivered once, in order, never twice. */
  onEvent: (event: SessionEvent) => void;
  /** The message being generated: the whole text so far, not a delta. */
  onFragment: (text: string) => void;
  onStatus: (status: "connecting" | "live" | "lost") => void;
};

const PERSISTED_TYPES: readonly string[] = [
  "session.started",
  "child.message",
  "tutor.message",
  "exercise.served",
  "hint.delivered",
  "correction",
];

export class SessionStream {
  private source: EventSource | null = null;
  private cursor: number;
  private closed = false;

  constructor(
    private readonly tutorSessionId: string,
    private readonly handlers: StreamHandlers,
    since = 0,
  ) {
    this.cursor = since;
  }

  get lastEventId(): number {
    return this.cursor;
  }

  open(): void {
    if (this.closed) return;
    this.close(false);
    this.handlers.onStatus("connecting");

    const url = buildUrl("streamEvents", {
      params: { tutor_session_id: this.tutorSessionId },
      query: { since: this.cursor },
    });
    const source = new EventSource(url, { withCredentials: true });
    this.source = source;

    source.onopen = () => this.handlers.onStatus("live");
    source.onerror = () => {
      // EventSource retries by itself, replaying `Last-Event-ID`. Saying "lost"
      // is honest — SPEC §8.3 — and costs nothing, because the same object will
      // say "live" again on its own.
      this.handlers.onStatus("lost");
    };

    for (const type of PERSISTED_TYPES) {
      source.addEventListener(type, (raw) => this.receive(type, raw as MessageEvent<string>));
    }
    source.addEventListener("tutor.fragment", (raw) => {
      const data = parse((raw as MessageEvent<string>).data);
      const text = typeof data?.text === "string" ? data.text : "";
      if (text) this.handlers.onFragment(text);
    });
  }

  private receive(type: string, raw: MessageEvent<string>): void {
    const data = parse(raw.data);
    if (!data) return;
    const id = typeof data.id === "number" ? data.id : Number(raw.lastEventId);
    if (!Number.isFinite(id) || id <= this.cursor) return; // already seen
    this.cursor = id;
    this.handlers.onEvent({
      id,
      seq: typeof data.seq === "number" ? data.seq : id,
      type: type as SessionEventType,
      payload: (data.payload as Record<string, unknown>) ?? {},
      at: typeof data.at === "string" ? data.at : null,
    });
  }

  /** Reopen from the cursor — used when the network comes back. */
  resume(): void {
    if (this.closed) return;
    this.open();
  }

  close(permanently = true): void {
    if (permanently) this.closed = true;
    this.source?.close();
    this.source = null;
  }
}

function parse(data: string): Record<string, unknown> | null {
  try {
    return JSON.parse(data) as Record<string, unknown>;
  } catch {
    return null;
  }
}
